import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../../api";
import type { AgentServiceAccountCreate } from "../../../generated/core-api";

export function serviceAccountsQueryKey(agentId: string) {
  return ["serviceAccounts", agentId] as const;
}

export function useServiceAccounts(agentId: string | null, enabled = true) {
  return useQuery({
    queryKey: serviceAccountsQueryKey(agentId ?? ""),
    queryFn: () => api.listServiceAccounts(agentId as string),
    enabled: enabled && !!agentId,
  });
}

export function useServiceAccountsForAgents(
  agentIds: string[],
  enabled = true,
) {
  return useQueries({
    queries: agentIds.map((agentId) => ({
      queryKey: serviceAccountsQueryKey(agentId),
      queryFn: () => api.listServiceAccounts(agentId),
      enabled: enabled && agentIds.length > 0,
    })),
  });
}

export function useCreateServiceAccount(agentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AgentServiceAccountCreate) =>
      api.createServiceAccount(agentId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: serviceAccountsQueryKey(agentId),
      });
    },
  });
}

export function useDeleteServiceAccount(agentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (serviceAccountId: string) =>
      api.deleteServiceAccount(agentId, serviceAccountId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: serviceAccountsQueryKey(agentId),
      });
    },
  });
}

export function useGrantCapability(agentId: string, serviceAccountId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (capabilityId: string) =>
      api.grantCapability(agentId, serviceAccountId, capabilityId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: serviceAccountsQueryKey(agentId),
      });
    },
  });
}

export function useRevokeCapability(agentId: string, serviceAccountId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (capabilityId: string) =>
      api.revokeCapability(agentId, serviceAccountId, capabilityId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: serviceAccountsQueryKey(agentId),
      });
    },
  });
}

export function useSyncCapability(agentId: string, serviceAccountId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (capabilityId: string) =>
      api.syncCapability(agentId, serviceAccountId, capabilityId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: serviceAccountsQueryKey(agentId),
      });
    },
  });
}

export function useSyncAllCapabilities(agentId: string, serviceAccountId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.syncAllCapabilitySnapshots(agentId, serviceAccountId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: serviceAccountsQueryKey(agentId),
      });
    },
  });
}
